import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import MetaHead from '@/components/MetaHead';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import './giftPage.css';
import { useAuthStore } from '@/store/auth';
import { useModalStore } from '@/store/modal';
import { giftsApi } from '@/lib/api/gifts';

function GiftIcon() {
  return <svg aria-hidden="true" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11h16v10H4zM3 7h18v4H3zm9 0v14" /><path d="M12 7H8.5A2.5 2.5 0 1 1 11 4.5L12 7Zm0 0h3.5A2.5 2.5 0 1 0 13 4.5L12 7Z" /></svg>;
}
function GiftArtwork() {
  return <figure className="gg-artwork" aria-label="A digital gift of one year of Gab AI Plus">
    <div className="gg-gift-card">
      <img src="/brand-assets/gab-ai-mark-white.svg" alt="" width="36" height="36" />
      <span className="gg-card-plus" aria-hidden="true" />
      <div className="gg-card-message">A year of<br />possibility.</div>
      <div className="gg-card-footer"><span>Gab AI Plus</span><span>1 year</span></div>
    </div>
    <figcaption>A digital gift. Ready to share.</figcaption>
  </figure>;
}

const giftLink = code => `${window.location.origin}/gift#code=${code}`;
const codeFromLocation = () => typeof window === 'undefined' ? '' : new URLSearchParams(window.location.hash.slice(1)).get('code') || '';
const rememberGiftReads = (snapshots, rows, version) => rows.map(gift => {
  const current = snapshots.get(gift.id);
  if (current && current.version > version) return current.gift;
  snapshots.set(gift.id, { version, gift });
  return gift;
});

export default function GiftPage(props) {
  const userId = useAuthStore(state => state.user?.id || state.user?._id);
  const token = useAuthStore(state => state.token);
  const generation = useAuthStore(state => state._sessionGeneration);
  const [code, setCode] = useState(codeFromLocation);
  useEffect(() => {
    const update = () => setCode(codeFromLocation());
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, []);
  // Private links and async results belong to one account and one selected
  // gift. Remount before rendering a different owner's or link's state.
  return <GiftPageContent key={JSON.stringify([userId, Boolean(token), generation, code])} {...props} code={code} />;
}

function GiftPageContent({ purchase = false, code }) {
  const { colors, theme } = useTheme();
  const router = useRouter();
  const { order } = useLocalSearchParams();
  const user = useAuthStore(state => state.user);
  const token = useAuthStore(state => state.token);
  const expired = useAuthStore(state => state.isTokenExpired);
  const authenticated = Boolean(token) && !expired();
  const [catalog, setCatalog] = useState(null);
  const [gifts, setGifts] = useState([]);
  const [historyCursor, setHistoryCursor] = useState(null);
  const [hasOlderGifts, setHasOlderGifts] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const key = useRef(null);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  const historyVersion = useRef(0);
  const olderInFlight = useRef(false);
  const giftReadVersion = useRef(0);
  const appliedHistoryVersion = useRef(0);
  const giftSnapshots = useRef(new Map());
  const signIn = () => useModalStore.getState().actions.openModal('AuthCTAModal', { source: 'gift' });
  const reload = useCallback(async () => {
    const tasks = [];
    if (purchase && authenticated) {
      const version = ++historyVersion.current;
      const readVersion = ++giftReadVersion.current;
      tasks.push(giftsApi.catalog().then(setCatalog), giftsApi.list().then(rows => {
        if (!mounted.current || version !== historyVersion.current) return;
        appliedHistoryVersion.current = readVersion;
        // A checkout poll begun after this history read owns its newer result,
        // including when the returned order falls outside the first page.
        const refreshed = rememberGiftReads(giftSnapshots.current, rows, readVersion);
        const newer = [...giftSnapshots.current.values()].filter(entry => entry.version > readVersion).map(entry => entry.gift);
        const ids = new Set(newer.map(gift => gift.id));
        setGifts([...newer, ...refreshed.filter(gift => !ids.has(gift.id))]);
        setHistoryCursor(rows.at(-1)?.id || null);
        setHasOlderGifts(rows.length === 20);
      }));
    }
    if (!purchase && code) tasks.push(giftsApi.preview(code).then(setPreview));
    await Promise.all(tasks);
  }, [authenticated, code, purchase]);
  useEffect(() => {
    let active = true;
    mounted.current = true;
    reload().catch(e => active && setError(e.message)).finally(() => active && setLoading(false));
    return () => { active = false; mounted.current = false; };
  }, [reload]);
  // A return URL is only a cue to poll the authenticated order. It never proves payment.
  useEffect(() => {
    if (!purchase || !order || !authenticated || !/^[a-f0-9]{24}$/.test(String(order))) return undefined;
    let active = true, timer, attempts = 0;
    const check = async () => {
      const readVersion = ++giftReadVersion.current;
      try {
        const gift = await giftsApi.status(order);
        if (!active) return;
        if (readVersion >= appliedHistoryVersion.current && readVersion >= (giftSnapshots.current.get(gift.id)?.version || 0)) {
          rememberGiftReads(giftSnapshots.current, [gift], readVersion);
          setGifts(previous => [gift, ...previous.filter(g => g.id !== gift.id)]);
        }
        if (gift.status === 'pending' && ++attempts < 30) timer = setTimeout(check, 4000);
      } catch (e) { if (active) setError(e.message); }
    };
    check();
    return () => { active = false; clearTimeout(timer); };
  }, [order, authenticated, purchase]);
  async function loadOlderGifts() {
    if (!historyCursor || olderInFlight.current) return;
    olderInFlight.current = true;
    const version = historyVersion.current;
    const readVersion = ++giftReadVersion.current;
    setLoadingOlder(true);
    setError('');
    try {
      const rows = await giftsApi.list(historyCursor);
      if (!mounted.current || version !== historyVersion.current) return;
      const refreshed = rememberGiftReads(giftSnapshots.current, rows, readVersion);
      const updates = new Map(refreshed.map(gift => [gift.id, gift]));
      setGifts(previous => {
        const existing = new Set(previous.map(gift => gift.id));
        return [...previous.map(gift => updates.get(gift.id) || gift), ...refreshed.filter(gift => !existing.has(gift.id))];
      });
      setHistoryCursor(rows.at(-1)?.id || null);
      setHasOlderGifts(rows.length === 20);
    } catch (e) {
      if (mounted.current && version === historyVersion.current) setError(e.message);
    } finally {
      olderInFlight.current = false;
      if (mounted.current) setLoadingOlder(false);
    }
  }
  async function act(action) {
    if (!authenticated) { signIn(); return; }
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError(''); setNotice('');
    try {
      if (action === 'buy') {
        key.current ||= crypto.randomUUID();
        const gift = await giftsApi.checkout(key.current);
        if (!mounted.current) return;
        if (gift.status === 'pending' && gift.checkoutUrl) {
          const url = new URL(gift.checkoutUrl);
          if (url.protocol !== 'https:' || !(url.hostname === 'whop.com' || url.hostname.endsWith('.whop.com'))) throw new Error('Checkout could not be opened.');
          window.location.assign(url.href);
        } else { await reload(); key.current = null; }
      } else if (action === 'redeem') {
        const gift = await giftsApi.redeem(code);
        if (!mounted.current) return;
        setPreview({ status: 'redeemed', redeemedByYou: true, expiresAt: gift.expiresAt });
        await useAuthStore.getState().fetchUser();
        setNotice('Your year of Plus is ready. Enjoy!');
      }
    } catch (e) { setError(e.message); }
    finally { inFlight.current = false; setBusy(false); }
  }
  const date = value => new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  const Container = purchase ? View : ScrollView;
  const giftTheme = {
    colorScheme: isDarkTheme(theme) ? 'dark' : 'light',
    '--gg-ink': colors.text_color_primary,
    '--gg-muted': colors.text_color_secondary,
    '--gg-rule': colors.border_color_secondary,
    '--gg-fill': colors.solid_color_tertiary,
    '--gg-surface': colors.solid_color_primary,
    '--gg-error': colors.color_destructive,
  };
  return <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
    <MetaHead title="Gift a year of Gab AI Plus" description="Give someone a year of Gab AI Plus with a shareable gift link. One payment, no automatic renewal." noindex />
    <Container {...(!purchase ? { contentContainerStyle: { padding: 24, paddingTop: 124, paddingBottom: 48, alignItems: 'center' } } : {})}>
      <div className="gg-page" style={giftTheme}>
        <button className="gg-back" type="button" onClick={() => router.push(purchase ? '/settings?tab=subscription' : '/app')}>
          <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="m10 5-7 7 7 7M3 12h18" /></svg>
          {purchase ? 'Back to billing' : 'Back to Gab AI'}
        </button>
        {!!error && <p role="alert" className="gg-notice gg-error">{error}</p>}
        {!!notice && <p role="status" className="gg-notice">{notice}</p>}
        <section className="gg-hero" aria-label={purchase ? 'Gift Gab AI Plus' : 'Redeem Gab AI Plus'}>
          <div className="gg-intro">
            <span className="gg-eyebrow">{purchase ? 'Gift Gab AI Plus' : 'A gift for you'}</span>
            <h2>{purchase ? <>A little inspiration.<br />A whole year of Plus.</> : <>You’ve been given<br />a year of Plus.</>}</h2>
            <p className="gg-description">A thoughtful gift for someone with ideas to explore and things to make.</p>
            {loading && <ActivityIndicator accessibilityLabel="Loading gift" />}
            {purchase && (authenticated ? <div className="gg-purchase">
              {catalog && <p className="gg-price"><strong>{new Intl.NumberFormat('en-US', { style: 'currency', currency: catalog.currency }).format(catalog.price)}</strong><span>One year of Plus</span></p>}
              <p className="gg-payment-note">One payment. No automatic renewal.</p>
              <button className="gg-primary" type="button" disabled={busy || !catalog?.available} onClick={() => act('buy')}>{busy ? 'Opening checkout…' : 'Buy a Plus gift'}</button>
              <p className="gg-tax-note">Applicable taxes are shown at checkout.</p>
              {catalog && !catalog.available && <p className="gg-muted">Gift checkout is not available right now.</p>}
            </div> : <button className="gg-primary" type="button" onClick={signIn}>Sign in to manage gifts</button>)}
            {!purchase ? <section className="gg-redemption" aria-label="Your Plus gift">
              {!code && <p>Open the private gift link you received to redeem your year of Plus.</p>}
              <h3>One year of Plus</h3>
              <p>The year begins when you redeem. No payment details or automatic renewal.</p>
              {preview?.status === 'ready' && <>
                <p className="gg-muted">An existing subscription must finish before you can redeem this gift. Redeeming adds it to your account{authenticated && user?.email ? ` (${user.email})` : ''}.</p>
                <button className="gg-primary" type="button" disabled={busy} onClick={() => act('redeem')}>{busy ? 'Redeeming…' : authenticated ? 'Redeem my year of Plus' : 'Sign in or create an account to redeem'}</button>
              </>}
              {preview?.status === 'redeemed' && <p>{preview.redeemedByYou ? `Your Plus access runs through ${date(preview.expiresAt)}.` : 'This gift has already been redeemed.'}</p>}
              {['revoked', 'review'].includes(preview?.status) && <p>This gift is not available for redemption. Please contact Gab AI Support if you need help.</p>}
            </section> : null}
          </div>
          <GiftArtwork />
        </section>
        {purchase && <>
          <ol className="gg-steps" aria-label="How gifting works">
            {[
              ['Make it a gift.', 'One payment for a full year of Plus. No recurring charges.'],
              ['Share a little possibility.', 'After payment is confirmed, copy your private gift link and give it to your friend.'],
              ['Their year starts with them.', 'They can create an account when redeeming. Their year begins then; an unredeemed gift does not expire.'],
            ].map(([title, body], index) => <li key={title}><span className="gg-step-number" aria-hidden="true">0{index + 1}</span><h3>{title}</h3><p>{body}</p></li>)}
          </ol>
          <details className="gg-details"><summary>Giving to someone who already has a subscription?</summary><p>Recipients with an existing subscription can redeem after that subscription ends. This gift does not replace or cancel a plan.</p></details>
          {authenticated && <section className="gg-history" aria-label="Your gifts">
            <h3>Your gifts</h3>
            {!gifts.length && <div className="gg-empty"><GiftIcon /><p>Your purchased gifts will appear here.<br /><span>Return any time to copy a gift link.</span></p></div>}
            {gifts.map(gift => <article key={gift.id} className="gg-history-item">
              <div className="gg-history-heading"><span className="gg-history-icon"><GiftIcon /></span><h4>One year of Plus</h4><span className="gg-gift-status">{gift.status === 'ready' ? 'Ready to give' : gift.status === 'pending' ? 'Awaiting payment confirmation' : gift.status === 'redeemed' ? 'Redeemed' : 'No longer available'}</span></div>
              {gift.status === 'ready' && <div className="gg-history-detail">
                <p className="gg-gift-link">{giftLink(gift.code)}</p>
                <p className="gg-muted">Keep this link private: anyone with it can redeem the gift.</p>
                <button type="button" onClick={async () => {
                  try { await navigator.clipboard.writeText(giftLink(gift.code)); setNotice('Gift link copied.'); }
                  catch { setNotice('Select and copy the gift link above.'); }
                }}>Copy gift link</button>
              </div>}
              {gift.status === 'pending' && <div className="gg-history-detail"><p className="gg-muted">Payment confirmation may take a moment. If you paid, check again shortly.</p><button type="button" onClick={() => reload().catch(e => setError(e.message))}>Check payment status</button></div>}
              {gift.status === 'redeemed' && <p className="gg-history-detail gg-muted">Plus access runs through {date(gift.expiresAt)}.</p>}
            </article>)}
            {hasOlderGifts && <button type="button" disabled={loadingOlder} onClick={loadOlderGifts}>
              {loadingOlder ? 'Loading older gifts…' : 'Show older gifts'}
            </button>}
          </section>}
        </>}
      </div>
    </Container>
  </View>;
}
